import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  detectApiKeys,
  getFeatureAvailability,
  ApiKeyStatus,
  FeatureAvailability
} from './featureAvailabilityService';
import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasFetch } from '../api/atlasBackendClient';

interface FeatureAvailabilityContextType {
  apiKeyStatus: ApiKeyStatus;
  featureAvailability: FeatureAvailability;
  isInitialized: boolean;
}

const FeatureAvailabilityContext = createContext<FeatureAvailabilityContextType | undefined>(undefined);

interface FeatureAvailabilityProviderProps {
  children: ReactNode;
  userId?: string | null;
}

export function FeatureAvailabilityProvider({ children, userId }: FeatureAvailabilityProviderProps) {
  const [apiKeyStatus, setApiKeyStatus] = useState<ApiKeyStatus>({
    reactAppApiBaseUrl: false,
    reactAppAtlasBaseUrl: false,
    openaiApiKey: false,
    reactAppOpenaiApiKey: false,
    jiraBackendConfigured: false,
    hereApiKey: false,
    graphhopperApiKey: false,
    reactAppOpenrouteApiKey: false,
  });
  
  const [featureAvailability, setFeatureAvailability] = useState<FeatureAvailability>({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const detectedKeys = detectApiKeys();
      let jiraBackendConfigured = false;

      try {
        const response = await atlasFetch(`${getAtlasApiBaseUrl()}/api/jira/status`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const data = (await response.json()) as { configured?: boolean; status?: string };
          jiraBackendConfigured =
            data.configured === true || String(data.status || '').toLowerCase() === 'configured';
        }
      } catch {
        jiraBackendConfigured = false;
      }

      if (cancelled) {
        return;
      }

      const merged: ApiKeyStatus = {
        ...detectedKeys,
        jiraBackendConfigured,
      };
      setApiKeyStatus(merged);
      setFeatureAvailability(getFeatureAvailability(merged));
      setIsInitialized(true);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const value: FeatureAvailabilityContextType = {
    apiKeyStatus,
    featureAvailability,
    isInitialized
  };

  return (
    <FeatureAvailabilityContext.Provider value={value}>
      {children}
    </FeatureAvailabilityContext.Provider>
  );
}

export function useFeatureAvailabilityContext() {
  const context = useContext(FeatureAvailabilityContext);
  if (context === undefined) {
    throw new Error('useFeatureAvailabilityContext must be used within a FeatureAvailabilityProvider');
  }
  return context;
}

